import axiosStatic from 'axios';

/**
 * @description Использовать обновление токена.
 * @param {function(): string} resolveToken
 * @returns {{isUnauthorizedError:function,refresh:function}}
 */
const useRefresh = (resolveToken) => {
  const isAttempted = (config) => {
    const { __isRetryRequest: isRetry } = config;
    return isRetry === true;
  };
  const isUnauthorizedError = (error) => {
    if (error && error.response && error.response.status) {
      return error.response.status === 401;
    }
    return false;
  };
  let isRefreshing = false;
  let refreshQueue = [];
  const refresh = (config, onRefresh, responseError) => {
    if (isAttempted(config)) {
      return Promise.reject(new Error('Retry attempt already made', { cause: responseError }));
    }
    const currentToken = resolveToken();
    // eslint-disable-next-line no-underscore-dangle
    const sentToken = config.__sentToken;
    if (sentToken && currentToken !== sentToken) {
      Object.assign(config, { __isRetryRequest: true });
      return Promise.resolve(config);
    }
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshQueue.push({ config, resolve, reject });
      });
    }
    isRefreshing = true;
    return onRefresh()
      .then(() => {
        // eslint-disable-next-line no-shadow
        refreshQueue.forEach(({ resolve, config }) => {
          Object.assign(config, { __isRetryRequest: true });
          resolve(config);
        });
        refreshQueue = [];
        Object.assign(config, { __isRetryRequest: true });
        return config;
      })
      .catch((error) => {
        refreshQueue.forEach(({ reject }) => reject(error));
        refreshQueue = [];
        return Promise.reject(error);
      })
      .finally(() => {
        isRefreshing = false;
      });
  };
  return { isUnauthorizedError, refresh };
};

/**
 * @description Создать новый экземпляр клиента Axios.
 * @param {string}             baseURL            Базовый ресурс запросов.
 * @param {function(): string} resolveToken       Получить токен аутентификации.
 * @param {string}             defaultContentType Тип содержимого запроса по умолчанию.
 * @returns {import('axios').AxiosInstance}
 */
export function createAxios(
  baseURL,
  resolveToken,
  defaultContentType = 'application/json',
) {
  const headers = { 'Content-Type': defaultContentType };
  const instance = axiosStatic.create({ baseURL, headers });
  instance.interceptors.request.use((config) => {
    const token = resolveToken();
    if (token !== '') {
      Object.assign(config.headers, { Authorization: `Bearer ${token}` });
    }
    if ((config.headers['Content-Type'] || '') === '') {
      Object.assign(config.headers, { 'Content-Type': defaultContentType });
    }
    return config;
  });
  return instance;
}

/**
 * @description Создать новый экземпляр клиента Axios с установкой перехватчиков на ошибки.
 * @param {string}              baseURL
 * @param {function(): Promise} onRefresh
 * @param {function(): void}    onUnauthorized
 * @param {function(): string}  resolveToken
 * @returns {import('axios').AxiosInstance}
 */
export function createAxiosWithErrorHandlers(
  baseURL,
  onRefresh,
  onUnauthorized,
  resolveToken,
) {
  const { isUnauthorizedError, refresh } = useRefresh(resolveToken);
  const instance = createAxios(baseURL, resolveToken);
  instance.interceptors.request.use((config) => {
    const token = resolveToken();
    if (token !== '') {
      Object.assign(config, { __sentToken: token });
    }
    return config;
  });
  instance.interceptors.response.use(undefined, async (error) => {
    if (isUnauthorizedError(error)) {
      const { config } = error;
      try {
        const updatedConfig = await refresh(config, onRefresh, error);
        return instance(updatedConfig);
      } catch (refreshError) {
        onUnauthorized();
        return Promise.reject(refreshError);
      }
    }
    throw error;
  });
  return instance;
}
